// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * The common shape of a control-plane collection screen.
 *
 * Every area renders a heading, a capability gate, the complete asynchronous
 * state machine, an accessible table, and a bounded-collection summary. Sharing
 * that shape means no screen can quietly omit a permission-denied, empty, or
 * error state — the states are supplied here and the area contributes only its
 * data binding and its columns.
 *
 * The pagination summary is rendered because `API-001` bounds every collection:
 * an operator must be able to see that they are looking at a page, not the
 * whole population.
 */

import {
  AsyncSection,
  DataTable,
  PageHeader,
  RequireCapability,
  useSession,
  type DataTableColumn,
  type ListResponse,
} from '@eai/ui-shared';
import Typography from '@mui/material/Typography';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

/** Props for {@link ResourceScreen}. */
export interface ResourceScreenProps<TRow> {
  /** Page title, rendered as the single `h1`. */
  readonly title: string;
  /** Short description of what the screen shows. */
  readonly description: string;
  /** Capability required for the area to render. */
  readonly requiredCapability: string;
  /** Human-readable area name used in the denied state. */
  readonly area: string;
  /** The collection query. */
  readonly query: UseQueryResult<ListResponse<TRow>, unknown>;
  /** Column definitions. */
  readonly columns: readonly DataTableColumn<TRow>[];
  /** Stable React key for a row. */
  readonly rowKey: (row: TRow) => string;
  /** Heading shown when the collection is empty. */
  readonly emptyTitle: string;
  /** Guidance shown alongside the empty heading. */
  readonly emptyDescription: string;
  /** Optional content rendered above the table. */
  readonly children?: ReactNode;
}

/** Render a capability-gated, bounded collection screen. */
export function ResourceScreen<TRow>({
  title,
  description,
  requiredCapability,
  area,
  query,
  columns,
  rowKey,
  emptyTitle,
  emptyDescription,
  children,
}: ResourceScreenProps<TRow>): ReactNode {
  const { session } = useSession();

  return (
    <RequireCapability required={[requiredCapability]} area={area}>
      <PageHeader
        title={title}
        description={description}
        {...(session?.university_label === null || session?.university_label === undefined
          ? {}
          : { scopeLabel: session.university_label })}
      />
      {children}
      <AsyncSection
        query={query}
        loadingLabel={`Loading ${title.toLowerCase()}`}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        isEmpty={(data) => data.items.length === 0}
      >
        {(data) => (
          <>
            <DataTable
              caption={`${title} — page ${String(data.pagination.page)} of ${String(data.pagination.total_pages)}`}
              columns={columns}
              rows={data.items}
              rowKey={rowKey}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {data.items.length} of {data.pagination.total_items} records (page{' '}
              {data.pagination.page} of {data.pagination.total_pages}).
            </Typography>
          </>
        )}
      </AsyncSection>
    </RequireCapability>
  );
}
